import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel,
  IconButton, LinearProgress, Paper,
  BottomNavigation, BottomNavigationAction,
  useMediaQuery, useTheme, InputAdornment,
  Drawer, Pagination, Slider, Checkbox, FormControlLabel,
  Collapse, Tooltip
} from 'ui/mui';
import {
  ArrowBack, Search, FilterList, Close,
  ShoppingCart, Delete, ShowChart,
  TrendingUp, TrendingDown, TrendingFlat,
  ViewModule, History, Star, SportsMartialArts,
  ExpandMore, ExpandLess, Refresh, ArrowForward,
  StarBorder, StarRate, Loop, Assessment, LocalShipping
} from 'ui/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ReferenceLine, ResponsiveContainer
} from 'recharts';
import { supabase } from '../supabaseClient';
import './JapanCards.css';

const ALLOWED = ['randolph@pokerand.net', 'joe@pokerand.net'];
const JPY_GBP = 0.0047;
const HAMMER = 34;
const PAGE_SIZE = 48;
const BREAKEVEN_JPY = 4521;

const STATUS_FLOW = ['ordered', 'received', 'at_ace', 'graded', 'sold'];

function profit(gbp) {
  if (!gbp) return null;
  return +(HAMMER - gbp - 8.33 - (HAMMER*0.06 + HAMMER*0.0242 + 0.25)).toFixed(2);
}

// Result profit formula: hammer_price - cost_gbp - 8.33 - (hammer_price * 0.0842 + 0.25)
function resultProfit(hammerPrice, costGbp) {
  if (!hammerPrice || !costGbp) return null;
  return +(hammerPrice - costGbp - 8.33 - (hammerPrice * 0.0842 + 0.25)).toFixed(2);
}

// Rarity codes and order states as small badges; colour only where it means status.
function RChip({ rarity }) {
  return <Chip label={rarity||'?'} size="small" className="jc-rarity" />;
}

function StatusChip({ status }) {
  return <Chip label={status?.toUpperCase() || 'UNKNOWN'} size="small" className="jc-status" data-status={status || 'unknown'} />;
}

export default function JapanCards() {
  const navigate = useNavigate();
  const theme = useTheme();
  const mob = useMediaQuery(theme.breakpoints.down('sm'));
  const searchTimer = useRef(null);

  const [nav, setNav] = useState(0);
  const [perms, setPerms] = useState({});
  const [votes, setVotes] = useState({});
  const [loading, setLoading] = useState(false);

  // Card DB state
  const [cards, setCards] = useState([]);
  const [totalCards, setTotalCards] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [rarityF, setRarityF] = useState('ALL');
  const [voteF, setVoteF] = useState('ALL');
  const [stockF, setStockF] = useState('IN_STOCK');
  const [filterOpen, setFilterOpen] = useState(false);

  // Stats panel state
  const [statsExpanded, setStatsExpanded] = useState(true);
  const [stats, setStats] = useState({ total:0, inStock:0, yesCount:0, profitable:0, mostProfitable:null, underBreakeven:0 });

  // YES list
  const [yesCards, setYesCards] = useState([]);
  const [yesLoading, setYesLoading] = useState(false);

  // Purchase history
  const [purchases, setPurchases] = useState([]);
  const [purchLoaded, setPurchLoaded] = useState(false);
  const [purchSearch, setPurchSearch] = useState('');

  // Results history (Tab 4)
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsStats, setResultsStats] = useState({ total:0, avgGrade:0, totalHammer:0, totalProfit:0, avgProfit:0, best:null, grades:{} });

  // Order management (Tab 5)
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState('ALL');
  const [orderStats, setOrderStats] = useState({ totalInvested:0, atAce:0, pendingReceipt:0 });

  // Chart
  const [chartCard, setChartCard] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartPurch, setChartPurch] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  // Order / Result dialogs
  const [orderCard, setOrderCard] = useState(null);
  const [orderQty, setOrderQty] = useState(1);
  const [resultCard, setResultCard] = useState(null);
  const [resultData, setResultData] = useState({ grade:'', hammer:'', date:today(), certNumber:'', notes:'', reorderFlag:false });
  const [resultPurchHistory, setResultPurchHistory] = useState([]);
  const [hammerSlider, setHammerSlider] = useState(34);

  useEffect(() => { loadPerms(); loadVotes(); loadStats(); }, []);
  useEffect(() => { if (nav === 0) fetchCards(); }, [nav, page, rarityF, stockF, votes]);
  useEffect(() => { if (nav === 1) loadYes(); }, [nav, votes]);
  useEffect(() => { if (nav === 4) loadResults(); }, [nav]);
  useEffect(() => { if (nav === 5 && perms.can_order) loadOrders(); }, [nav, perms.can_order]);

  async function loadPerms() {
    const email = getEmail();
    if (!email) return;
    const { data } = await supabase.from('card_permissions').select('*').eq('email', email).single();
    setPerms(data || {});
  }

  async function loadVotes() {
    const { data } = await supabase.from('card_votes').select('pid,vote');
    if (data) {
      const m = {};
      data.forEach(v => { m[v.pid] = v.vote; });
      setVotes(m);
    }
  }

  async function loadStats() {
    // Fetch aggregated stats for the cards panel
    const { data: allCards, count } = await supabase.from('japan_cards').select('pid,price_jpy,price_gbp,in_stock,pokemon_en,name', { count: 'exact' });
    if (!allCards) return;
    
    const inStock = allCards.filter(c => c.in_stock);
    const profitable = inStock.filter(c => profit(c.price_gbp) > 0);
    const underBreakeven = inStock.filter(c => c.price_jpy && c.price_jpy < BREAKEVEN_JPY);
    
    let mostProfitable = null;
    let maxProfit = -Infinity;
    inStock.forEach(c => {
      const p = profit(c.price_gbp);
      if (p !== null && p > maxProfit) {
        maxProfit = p;
        mostProfitable = { name: c.pokemon_en || c.name?.split('【')[0], profit: p };
      }
    });

    // Fetch YES count
    const { data: votesData } = await supabase.from('card_votes').select('pid,vote');
    const yesCount = votesData ? votesData.filter(v => v.vote === 'yes').length : 0;

    setStats({
      total: count || 0,
      inStock: inStock.length,
      yesCount,
      profitable: profitable.length,
      mostProfitable,
      underBreakeven: underBreakeven.length
    });
  }

  const fetchCards = useCallback(async (searchOverride) => {
    setLoading(true);
    const s = searchOverride !== undefined ? searchOverride : search;
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let q = supabase.from('japan_cards').select('*', { count: 'exact' }).range(from, to);

    if (s.trim()) {
      q = q.or(`pokemon_en.ilike.%${s}%,name.ilike.%${s}%,pokemon_jp.ilike.%${s}%`);
    }

    if (rarityF !== 'ALL') q = q.eq('rarity', rarityF);
    if (stockF === 'IN_STOCK') q = q.eq('in_stock', true);
    if (stockF === 'HISTORICAL') q = q.eq('in_stock', false);

    q = q.order('price_jpy', { ascending: false });

    const { data, count, error } = await q;
    if (error) { setLoading(false); return; }

    let result = data || [];

    if (voteF === 'YES') result = result.filter(c => votes[c.pid] === 'yes');
    if (voteF === 'NO') result = result.filter(c => votes[c.pid] === 'no');
    if (voteF === 'UNVOTED') result = result.filter(c => !votes[c.pid]);

    setCards(result);
    setTotalCards(voteF === 'ALL' ? (count || 0) : result.length);
    setLoading(false);
  }, [search, page, rarityF, stockF, voteF, votes]);

  function handleSearch(val) {
    setSearch(val);
    setPage(1);
    if (val.trim() && stockF === 'IN_STOCK') setStockF('ALL');
    if (!val.trim() && stockF === 'ALL') setStockF('IN_STOCK');
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchCards(val), 400);
  }

  async function loadYes() {
    setYesLoading(true);
    const yesPids = Object.entries(votes).filter(([,v]) => v==='yes').map(([pid]) => pid);
    if (!yesPids.length) { setYesCards([]); setYesLoading(false); return; }
    const { data } = await supabase.from('japan_cards').select('*').in('pid', yesPids);
    setYesCards(data || []);
    setYesLoading(false);
  }

  async function loadPurchases() {
    if (purchLoaded) return;
    // Fetch purchases + all card metadata in parallel
    const [purchResult, cardResult] = await Promise.all([
      (async () => {
        let all = [], from = 0;
        while (true) {
          const { data } = await supabase.from('card_purchase_history').select('*')
            .order('order_date', { ascending: false }).range(from, from + 999);
          all = all.concat(data || []);
          if ((data||[]).length < 1000) break;
          from += 1000;
        }
        return all;
      })(),
      (async () => {
        // Fetch ALL cards (not just current page) for enrichment
        let all = [], from = 0;
        while (true) {
          const { data } = await supabase.from('japan_cards')
            .select('pid,name,pokemon_en,rarity,image_url,price_jpy').range(from, from + 999);
          all = all.concat(data || []);
          if ((data||[]).length < 1000) break;
          from += 1000;
        }
        return all;
      })()
    ]);
    const cardLookup = {};
    cardResult.forEach(c => { cardLookup[c.pid] = c; });
    const enriched = purchResult.map(r => {
      const c = cardLookup[r.pid];
      return { ...r, _name: c?.name, _en: c?.pokemon_en, _rarity: c?.rarity, _imageUrl: c?.image_url, _currentPrice: c?.price_jpy };
    });
    setPurchases(enriched);
    setPurchLoaded(true);
  }

  // FEATURE 1: Results History
  async function loadResults() {
    setResultsLoading(true);
    
    // Fetch results with card info
    const { data: resultsData } = await supabase
      .from('card_results')
      .select('*')
      .order('stream_date', { ascending: false });
    
    if (!resultsData) { setResultsLoading(false); return; }
    
    // Get card details for all result PIDs
    const pids = [...new Set(resultsData.map(r => r.pid))];
    const { data: cardsData } = await supabase
      .from('japan_cards')
      .select('pid,name,pokemon_en,rarity,image_url,price_gbp')
      .in('pid', pids);
    
    const cardMap = {};
    (cardsData || []).forEach(c => { cardMap[c.pid] = c; });
    
    const enriched = resultsData.map(r => ({
      ...r,
      _card: cardMap[r.pid] || {},
      _profit: resultProfit(r.hammer_price, cardMap[r.pid]?.price_gbp)
    }));
    
    setResults(enriched);
    
    // Calculate stats
    const grades = { 10:0, 9:0, 8:0, lower:0 };
    let totalGrade = 0, gradeCount = 0, totalHammer = 0, totalProfit = 0, profitCount = 0;
    let best = null, bestHammer = 0;
    
    enriched.forEach(r => {
      if (r.hammer_price) totalHammer += r.hammer_price;
      if (r.hammer_price > bestHammer) {
        bestHammer = r.hammer_price;
        best = { name: r._card?.pokemon_en || r._card?.name?.split('【')[0], hammer: r.hammer_price };
      }
      if (r.ace_grade) {
        const g = parseInt(r.ace_grade);
        totalGrade += g;
        gradeCount++;
        if (g === 10) grades[10]++;
        else if (g === 9) grades[9]++;
        else if (g === 8) grades[8]++;
        else grades.lower++;
      }
      if (r._profit !== null) {
        totalProfit += r._profit;
        profitCount++;
      }
    });
    
    setResultsStats({
      total: enriched.length,
      avgGrade: gradeCount ? +(totalGrade / gradeCount).toFixed(1) : 0,
      totalHammer: +totalHammer.toFixed(2),
      totalProfit: +totalProfit.toFixed(2),
      avgProfit: profitCount ? +(totalProfit / profitCount).toFixed(2) : 0,
      best,
      grades
    });
    
    setResultsLoading(false);
  }

  async function toggleFavourite(resultId, current) {
    await supabase.from('card_results').update({ is_favourite: !current }).eq('id', resultId);
    setResults(prev => prev.map(r => r.id === resultId ? { ...r, is_favourite: !current } : r));
  }

  async function toggleReorder(resultId, current) {
    await supabase.from('card_results').update({ reorder_flag: !current }).eq('id', resultId);
    setResults(prev => prev.map(r => r.id === resultId ? { ...r, reorder_flag: !current } : r));
  }

  // FEATURE 2: Order Management
  async function loadOrders() {
    setOrdersLoading(true);
    
    const { data: ordersData } = await supabase
      .from('card_orders')
      .select('*')
      .order('ordered_at', { ascending: false });
    
    if (!ordersData) { setOrdersLoading(false); return; }
    
    // Get card details
    const pids = [...new Set(ordersData.map(o => o.pid))];
    const { data: cardsData } = await supabase
      .from('japan_cards')
      .select('pid,name,pokemon_en,rarity,image_url')
      .in('pid', pids);
    
    const cardMap = {};
    (cardsData || []).forEach(c => { cardMap[c.pid] = c; });
    
    const enriched = ordersData.map(o => ({
      ...o,
      _card: cardMap[o.pid] || {}
    }));
    
    setOrders(enriched);
    
    // Calculate order stats
    let totalInvested = 0, atAce = 0, pendingReceipt = 0;
    enriched.forEach(o => {
      if (o.status !== 'sold') totalInvested += (o.cost_gbp || 0);
      if (o.status === 'at_ace') atAce++;
      if (o.status === 'ordered') pendingReceipt++;
    });
    
    setOrderStats({ totalInvested: +totalInvested.toFixed(2), atAce, pendingReceipt });
    setOrdersLoading(false);
  }

  async function advanceOrderStatus(orderId, currentStatus) {
    const currentIdx = STATUS_FLOW.indexOf(currentStatus);
    if (currentIdx === -1 || currentIdx >= STATUS_FLOW.length - 1) return;
    const nextStatus = STATUS_FLOW[currentIdx + 1];
    
    const { error } = await supabase
      .from('card_orders')
      .update({ status: nextStatus })
      .eq('id', orderId);
    
    if (!error) {
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: nextStatus } : o));
      // Recalculate stats
      loadOrders();
    }
  }

  const filteredOrders = useMemo(() => {
    if (orderStatusFilter === 'ALL') return orders;
    return orders.filter(o => o.status === orderStatusFilter);
  }, [orders, orderStatusFilter]);

  const orderCounts = useMemo(() => {
    const counts = { ALL: orders.length };
    STATUS_FLOW.forEach(s => { counts[s] = orders.filter(o => o.status === s).length; });
    return counts;
  }, [orders]);

  async function openChart(card) {
    setChartCard(card);
    setChartLoading(true);
    const [{ data: ph }, { data: pu }] = await Promise.all([
      supabase.from('card_price_history').select('price_jpy,checked_at').eq('pid', card.pid).order('checked_at'),
      supabase.from('card_purchase_history').select('*').eq('pid', card.pid).order('order_date'),
    ]);
    const pts = (ph||[]).map(p => ({ date: fmtDate(p.checked_at), price: p.price_jpy }));
    pts.push({ date: 'Now', price: card.price_jpy });
    setChartData(pts);
    setChartPurch(pu || []);
    setChartLoading(false);
  }

  async function deleteVote(pid) {
    await supabase.from('card_votes').delete().eq('pid', pid).eq('voter_email', 'randolph@pokerand.net');
    setVotes(prev => { const u={...prev}; delete u[pid]; return u; });
    setYesCards(prev => prev.filter(c => c.pid !== pid));
  }

  async function submitOrder() {
    const { error } = await supabase.from('card_orders').insert({
      pid: orderCard.pid, qty: orderQty,
      cost_jpy: orderCard.price_jpy * orderQty,
      cost_gbp: +(orderCard.price_gbp * orderQty).toFixed(2),
      ordered_by: getEmail(),
    });
    if (!error) setOrderCard(null);
    else alert('Error: ' + error.message);
  }

  // FEATURE 6: Enhanced Result Entry
  async function openResultEntry(card) {
    setResultCard(card);
    setResultData({ grade:'', hammer:'', date:today(), certNumber:'', notes:'', reorderFlag:false });
    setHammerSlider(34);
    
    // Load purchase history for this card
    const { data } = await supabase
      .from('card_purchase_history')
      .select('*')
      .eq('pid', card.pid)
      .order('order_date', { ascending: false });
    setResultPurchHistory(data || []);
  }

  async function submitResult() {
    const { error } = await supabase.from('card_results').insert({
      pid: resultCard.pid,
      ace_grade: resultData.grade ? String(resultData.grade) : null,
      hammer_price: resultData.hammer ? +resultData.hammer : null,
      stream_date: resultData.date || today(),
      cert_number: resultData.certNumber || null,
      notes: resultData.notes || null,
      reorder_flag: resultData.reorderFlag,
      entered_by: getEmail(),
    });
    if (!error) { 
      setResultCard(null); 
      setResultData({ grade:'', hammer:'', date:today(), certNumber:'', notes:'', reorderFlag:false });
      setResultPurchHistory([]);
    }
    else alert('Error: ' + error.message);
  }

  // Filtered purchase history (client-side, already loaded)
  const filteredPurch = useMemo(() => {
    if (!purchSearch) return purchases;
    const s = purchSearch.toLowerCase();
    return purchases.filter(r =>
      r._en?.toLowerCase().includes(s) ||
      r._name?.toLowerCase().includes(s) ||
      r.pid.includes(s)
    );
  }, [purchases, purchSearch]);

  // Auth gate
  if (!ALLOWED.includes(getEmail()?.toLowerCase())) {
    return (
      <div className="rc-page jc-page">
        <p className="jc-denied">Access restricted.</p>
        <Button onClick={() => navigate('/dashboard')} startIcon={<ArrowBack />} className="jc-denied__back">Back</Button>
      </div>
    );
  }

  const yesCount = Object.values(votes).filter(v=>v==='yes').length;
  const totalPages = Math.ceil(totalCards / PAGE_SIZE);

  // Desktop tabs - add Results History (4) and Order Management (5, admin only)
  const desktopTabs = [
    ['Cards', 0], ['YES List', 1], ['Results Entry', 2], ['Purchase History', 3], ['Stream Results', 4]
  ];
  if (perms.can_order) desktopTabs.push(['Order Management', 5]);

  return (
    <div className="rc-page jc-page">

      {/* HEADER */}
      <header className="jc-header">
        <IconButton onClick={() => navigate('/dashboard')} className="jc-back">
          <ArrowBack />
        </IconButton>
        <div className="jc-header__row">
          <div className="jc-header__title">
            <p className="rc-eyebrow">Buying</p>
            <h1 className="rc-page-title"><span className="jc-flag">🇯🇵</span> Japan Cards</h1>
          </div>
          <div className="jc-header__end">
            {yesCount > 0 && <Chip label={`${yesCount} YES`} size="small" className="jc-yes-count" />}
          </div>
        </div>
      </header>

      {/* DESKTOP TABS */}
      {!mob && (
        <div className="rc-tabs jc-tabs">
          {desktopTabs.map(([label, idx]) => (
            <button type="button" key={idx} onClick={() => { setNav(idx); if(idx===3) loadPurchases(); }}
              className={`rc-tab${nav===idx ? ' is-active' : ''}`}>
              {label}{idx===1&&yesCount>0?` (${yesCount})`:''}
            </button>
          ))}
        </div>
      )}

      {/* ── CARD DATABASE (Tab 0) ── */}
      {nav === 0 && (
        <section className="jc-view">

          {/* FEATURE 3: Stats Panel */}
          <div className="jc-stats" data-open={statsExpanded ? 'true' : 'false'}>
            <div className="jc-stats__head" onClick={() => setStatsExpanded(!statsExpanded)}>
              <div className="jc-stats__title">
                <span className="jc-stats__icon"><Assessment size={18} /></span>
                <p>Stats Overview</p>
              </div>
              {statsExpanded ? <ExpandLess /> : <ExpandMore />}
            </div>
            <Collapse in={statsExpanded}>
              <div className="jc-figures">
                <span className="jc-figure"><strong>{stats.total}</strong> cards</span>
                <span className="jc-figure"><strong>{stats.inStock}</strong> in stock</span>
                <span className="jc-figure"><strong>{stats.yesCount}</strong> YES</span>
                <span className="jc-figure" data-tone="good"><strong>{stats.profitable}</strong> profitable</span>
                <span className="jc-figure"><strong>{stats.underBreakeven}</strong> &lt; ¥{BREAKEVEN_JPY.toLocaleString()}</span>
              </div>
              {stats.mostProfitable && (
                <div className="jc-stats__note">
                  <p>
                    Most profitable: <span className="jc-good">{stats.mostProfitable.name}</span> (+£{stats.mostProfitable.profit})
                  </p>
                </div>
              )}
            </Collapse>
          </div>

          <div className="jc-search">
            <TextField
              placeholder="Charizard, Greninja, リザードン…"
              value={search} onChange={e => handleSearch(e.target.value)}
              size="small" fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search size={18}/></InputAdornment>,
                endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => handleSearch('')} className="jc-clear"><Close fontSize="small"/></IconButton></InputAdornment>,
              }}
            />
            <IconButton onClick={() => setFilterOpen(true)} className="jc-filter-button"
              data-active={(rarityF!=='ALL'||voteF!=='ALL'||stockF!=='IN_STOCK') ? 'true' : undefined}>
              <FilterList />
            </IconButton>
          </div>

          {loading && <LinearProgress color="error" />}

          <span className="jc-caption">
            {totalCards} cards · page {page} of {totalPages||1}
          </span>

          <div className="jc-grid">
            {cards.map(card => {
              const vote = votes[card.pid];
              const p = profit(card.price_gbp);
              const pc = p > 15 ? 'good' : p > 5 ? 'fair' : p > 0 ? 'fair' : 'poor';
              return (
                <div key={card.pid} onClick={() => openChart(card)} className="jc-card rc-lit" data-vote={vote || undefined}>
                  <div className="jc-card__vote" />
                  <div className="jc-card__media">
                    <img src={card.image_url} alt={card.name} loading="lazy" />
                    {!card.in_stock && (
                      <div className="jc-card__flag">
                        <p>HISTORICAL</p>
                      </div>
                    )}
                  </div>
                  <div className="jc-card__body">
                    <div className="jc-card__meta">
                      <RChip rarity={card.rarity} />
                      {p !== null && <p className="jc-profit" data-tone={pc}>{p>0?'+':''}£{p}</p>}
                    </div>
                    <p className="jc-card__name" data-native={card.pokemon_en ? undefined : 'true'}>
                      {card.pokemon_en || card.name.split('【')[0]}
                    </p>
                    <p className="jc-card__price">¥{card.price_jpy?.toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="jc-pages">
              <Pagination count={totalPages} page={page} onChange={(e,v)=>{ setPage(v); window.scrollTo(0,0); }} />
            </div>
          )}
        </section>
      )}

      {/* ── YES LIST (Tab 1) ── */}
      {nav === 1 && (
        <section className="jc-view">
          <p className="jc-caption">{yesCards.length} cards</p>
          {yesLoading && <LinearProgress color="error" />}
          <div className="jc-rows">
            {yesCards.map(card => {
              const p = profit(card.price_gbp);
              return (
                <div key={card.pid} className="jc-row">
                  <img src={card.image_url} alt="" loading="lazy" className="jc-thumb" />
                  <div className="jc-row__main">
                    <p className="jc-row__title">{card.pokemon_en || card.name.split('【')[0]}</p>
                    <p className="jc-row__sub">{card.name}</p>
                    <div className="jc-row__meta">
                      <RChip rarity={card.rarity} />
                      <p className="jc-row__price">¥{card.price_jpy?.toLocaleString()}</p>
                      {p !== null && <p className="jc-profit" data-tone={p>0?'good':'bad'}>{p>0?'+':''}£{p}</p>}
                    </div>
                  </div>
                  <div className="jc-row__actions">
                    <IconButton size="small" onClick={() => openChart(card)} className="jc-icon"><ShowChart fontSize="small"/></IconButton>
                    <IconButton size="small" onClick={() => { setOrderCard(card); setOrderQty(1); }} className="jc-icon"><ShoppingCart fontSize="small"/></IconButton>
                    <IconButton size="small" onClick={() => deleteVote(card.pid)} className="jc-icon jc-icon--danger"><Delete fontSize="small"/></IconButton>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── RESULTS ENTRY (Tab 2) ── */}
      {nav === 2 && (
        <section className="jc-view">
          <TextField placeholder="Search card to enter result…" value={search} onChange={e => handleSearch(e.target.value)}
            size="small" fullWidth />
          <div className="jc-grid jc-grid--entry">
            {cards.slice(0,24).map(card => (
              <div key={card.pid} className="jc-card jc-card--entry">
                <div className="jc-card__media jc-card__media--entry">
                  <img src={card.image_url} alt="" loading="lazy" />
                </div>
                <div className="jc-card__body">
                  <p className="jc-card__name">
                    {card.pokemon_en || card.name.split('【')[0]}
                  </p>
                  <Button variant="outlined" size="small" fullWidth
                    onClick={() => openResultEntry(card)}>
                    Enter Result
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── PURCHASE HISTORY (Tab 3) ── */}
      {nav === 3 && (
        <section className="jc-view">
          <p className="jc-caption">{purchLoaded ? `${purchases.length} line items` : 'Loading…'}</p>
          <TextField placeholder="Search Charizard, Greninja…" value={purchSearch}
            onChange={e => setPurchSearch(e.target.value)} size="small" fullWidth
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search size={18}/></InputAdornment>,
            }} />
          {!purchLoaded && <LinearProgress color="error" />}
          {purchLoaded && <PurchaseCarousel purchases={filteredPurch} />}
          <div className="jc-rows jc-rows--tight">
            <PurchaseList purchases={filteredPurch} onChart={openChart} />
          </div>
        </section>
      )}

      {/* ── FEATURE 1: STREAM RESULTS (Tab 4) ── */}
      {nav === 4 && (
        <section className="jc-view">
          {/* Stats Dashboard */}
          <div className="rc-stats jc-stat-grid">
            <div className="rc-stat">
              <span className="rc-stat__label">Cards Graded</span>
              <span className="rc-stat__value">{resultsStats.total}</span>
            </div>
            <div className="rc-stat">
              <span className="rc-stat__label">Avg Grade</span>
              <span className="rc-stat__value">{resultsStats.avgGrade}</span>
            </div>
            <div className="rc-stat">
              <span className="rc-stat__label">Total Hammer</span>
              <span className="rc-stat__value">£{resultsStats.totalHammer}</span>
            </div>
            <div className="rc-stat">
              <span className="rc-stat__label">Total Profit</span>
              <span className="rc-stat__value" data-tone={resultsStats.totalProfit > 0 ? 'good' : 'bad'}>
                {resultsStats.totalProfit > 0 ? '+' : ''}£{resultsStats.totalProfit}
              </span>
            </div>
            <div className="rc-stat">
              <span className="rc-stat__label">Avg Profit/Card</span>
              <span className="rc-stat__value" data-tone={resultsStats.avgProfit > 0 ? 'good' : 'bad'}>
                {resultsStats.avgProfit > 0 ? '+' : ''}£{resultsStats.avgProfit}
              </span>
            </div>
          </div>

          {/* Grade breakdown + Best hammer */}
          <div className="jc-chips">
            <Chip icon={<Star size={14} />} label={`${resultsStats.grades[10] || 0} ACE 10`} size="small" className="jc-tally" data-grade="10" />
            <Chip label={`${resultsStats.grades[9] || 0} ACE 9`} size="small" className="jc-tally" data-grade="9" />
            <Chip label={`${resultsStats.grades[8] || 0} ACE 8`} size="small" className="jc-tally" />
            <Chip label={`${resultsStats.grades.lower || 0} lower`} size="small" className="jc-tally" />
            {resultsStats.best && (
              <Chip label={`Best: ${resultsStats.best.name} (£${resultsStats.best.hammer})`} size="small" className="jc-tally jc-tally--best" />
            )}
          </div>

          {resultsLoading && <LinearProgress color="error" />}

          {/* Results list */}
          <div className="jc-rows">
            {results.map(r => (
              <div key={r.id} className="jc-row" data-flag={r.reorder_flag ? 'reorder' : undefined}>
                <img src={r._card?.image_url} alt="" loading="lazy" className="jc-thumb" />
                <div className="jc-row__main">
                  <p className="jc-row__title">
                    {r._card?.pokemon_en || r._card?.name?.split('【')[0] || r.pid}
                  </p>
                  <div className="jc-row__meta">
                    {r._card?.rarity && <RChip rarity={r._card.rarity} />}
                    {r.ace_grade && (
                      <Chip
                        label={`ACE ${r.ace_grade}`}
                        size="small"
                        className="jc-grade"
                        data-grade={r.ace_grade}
                      />
                    )}
                    {r.hammer_price && <p className="jc-row__money">£{r.hammer_price}</p>}
                    {r._profit !== null && (
                      <p className="jc-profit" data-tone={r._profit > 0 ? 'good' : 'bad'}>
                        ({r._profit > 0 ? '+' : ''}£{r._profit} profit)
                      </p>
                    )}
                  </div>
                  <div className="jc-row__meta">
                    <p className="jc-row__date">{r.stream_date}</p>
                    {r.cert_number && <p className="jc-row__cert">#{r.cert_number}</p>}
                  </div>
                </div>
                <div className="jc-row__actions jc-row__actions--stack">
                  <Tooltip title="Favourite">
                    <IconButton size="small" onClick={() => toggleFavourite(r.id, r.is_favourite)}
                      className="jc-icon jc-toggle" data-on={r.is_favourite ? 'true' : undefined}>
                      {r.is_favourite ? <StarRate fontSize="small" /> : <StarBorder fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reorder">
                    <IconButton size="small" onClick={() => toggleReorder(r.id, r.reorder_flag)}
                      className="jc-icon jc-toggle" data-on={r.reorder_flag ? 'true' : undefined}>
                      <Loop fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── FEATURE 2: ORDER MANAGEMENT (Tab 5, admin only) ── */}
      {nav === 5 && perms.can_order && (
        <section className="jc-view">
          {/* Order Stats */}
          <div className="rc-stats jc-stat-grid jc-stat-grid--three">
            <div className="rc-stat">
              <span className="rc-stat__label">Total Invested</span>
              <span className="rc-stat__value">£{orderStats.totalInvested}</span>
            </div>
            <div className="rc-stat">
              <span className="rc-stat__label">At ACE</span>
              <span className="rc-stat__value">{orderStats.atAce}</span>
            </div>
            <div className="rc-stat">
              <span className="rc-stat__label">Pending Receipt</span>
              <span className="rc-stat__value" data-tone="waiting">{orderStats.pendingReceipt}</span>
            </div>
          </div>

          {/* Status filter chips */}
          <div className="jc-chips">
            {['ALL', ...STATUS_FLOW.slice(0, -1)].map(status => (
              <Chip
                key={status}
                label={`${status === 'ALL' ? 'ALL' : status.toUpperCase()} (${orderCounts[status] || 0})`}
                size="small"
                onClick={() => setOrderStatusFilter(status)}
                className={orderStatusFilter === status ? 'is-active' : undefined}
              />
            ))}
          </div>

          {ordersLoading && <LinearProgress color="error" />}

          {/* Orders list */}
          <div className="jc-rows">
            {filteredOrders.map(o => (
              <div key={o.id} className="jc-row">
                <img src={o._card?.image_url} alt="" loading="lazy" className="jc-thumb" />
                <div className="jc-row__main">
                  <p className="jc-row__title">
                    {o._card?.pokemon_en || o._card?.name?.split('【')[0] || o.pid}
                  </p>
                  <div className="jc-row__meta">
                    {o._card?.rarity && <RChip rarity={o._card.rarity} />}
                    <StatusChip status={o.status} />
                    <p className="jc-row__qty">×{o.qty}</p>
                  </div>
                  <div className="jc-row__meta">
                    <p className="jc-row__price">¥{o.cost_jpy?.toLocaleString()}</p>
                    <p className="jc-row__money">£{o.cost_gbp}</p>
                    <p className="jc-row__date">{fmtDate(o.ordered_at)}</p>
                  </div>
                </div>
                <div className="jc-row__actions">
                  {o.status !== 'sold' && (
                    <Tooltip title={`Advance to ${STATUS_FLOW[STATUS_FLOW.indexOf(o.status) + 1]?.toUpperCase()}`}>
                      <IconButton size="small" onClick={() => advanceOrderStatus(o.id, o.status)} className="jc-icon">
                        <ArrowForward fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* MOBILE BOTTOM NAV */}
      {mob && (
        <Paper className="jc-bottom-bar">
          <BottomNavigation value={nav} onChange={(e,v) => { setNav(v); if(v===3) loadPurchases(); }}>
            <BottomNavigationAction label="Cards" value={0} icon={<ViewModule />} />
            <BottomNavigationAction label={yesCount?`YES (${yesCount})`:'YES'} value={1} icon={<Star />} />
            <BottomNavigationAction label="Results" value={2} icon={<SportsMartialArts />} />
            <BottomNavigationAction label="History" value={3} icon={<History />} />
          </BottomNavigation>
        </Paper>
      )}

      {/* FILTER DRAWER */}
      <Drawer anchor="bottom" open={filterOpen} onClose={() => setFilterOpen(false)}>
        <div className="jc-sheet">
          <div className="jc-sheet__head">
            <h2 className="jc-sheet__title">Filters</h2>
            <IconButton onClick={() => setFilterOpen(false)} className="jc-sheet__close"><Close /></IconButton>
          </div>
          {[
            ['RARITY', ['ALL','SSR','SAR','SR','UR','MA','HR','CHR','RRR','RR','S','SS'], rarityF, setRarityF, (r) => r==='ALL'?'All':r],
            ['VOTE STATUS', ['ALL','YES','NO','UNVOTED'], voteF, setVoteF, (v) => ({'ALL':'All','YES':'✅ YES','NO':'❌ NO','UNVOTED':'⬜ Unvoted'}[v])],
            ['STOCK', ['ALL','IN_STOCK','HISTORICAL'], stockF, setStockF, (v) => ({'ALL':'All cards','IN_STOCK':'In stock','HISTORICAL':'Historical'}[v])],
          ].map(([label, opts, val, setter, fmt]) => (
            <div key={label} className="jc-sheet__group">
              <p className="rc-label">{label}</p>
              <div className="jc-chips">
                {opts.map(o => (
                  <Chip key={o} label={fmt(o)} size="small" onClick={() => { setter(o); setPage(1); }}
                    className={val===o ? 'is-active' : undefined} />
                ))}
              </div>
            </div>
          ))}
          <Button fullWidth onClick={() => { setFilterOpen(false); fetchCards(); }} className="jc-sheet__apply">
            Apply Filters
          </Button>
        </div>
      </Drawer>

      {/* CHART DIALOG */}
      <Dialog open={!!chartCard} onClose={() => setChartCard(null)} fullWidth maxWidth="sm" fullScreen={mob}>
        <DialogTitle>
          <div className="jc-dialog-card">
            {chartCard?.image_url && <img src={chartCard.image_url} alt="" className="jc-dialog-card__img" />}
            <div className="jc-dialog-card__text">
              <p className="jc-dialog-card__name">{chartCard?.pokemon_en || chartCard?.name?.split('【')[0]}</p>
              <div className="jc-row__meta">
                {chartCard && <RChip rarity={chartCard.rarity} />}
                <p className="jc-row__price">¥{chartCard?.price_jpy?.toLocaleString()} · £{chartCard?.price_gbp}</p>
              </div>
            </div>
            {mob && <IconButton onClick={() => setChartCard(null)} className="jc-dialog-close"><Close /></IconButton>}
          </div>
        </DialogTitle>
        <DialogContent>
          {chartLoading ? <LinearProgress color="error" /> : (
            <div className="jc-chart">
              <div className="jc-chart__figures">
                {[['Current price', `¥${chartCard?.price_jpy?.toLocaleString()} / £${chartCard?.price_gbp}`],
                  ['Profit @ £34', `${profit(chartCard?.price_gbp)>0?'+':''}£${profit(chartCard?.price_gbp)}`]
                ].map(([l,v])=>(
                  <div key={l} className="jc-chart__figure">
                    <p className="jc-chart__label">{l}</p>
                    <p className="jc-chart__value" data-tone={l.includes('Profit')?(profit(chartCard?.price_gbp)>0?'good':'bad'):undefined}>{v}</p>
                  </div>
                ))}
              </div>
              <div className="jc-chart__plot">
                {chartData.length > 1 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData} margin={{ top:5,right:10,bottom:5,left:5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#272727" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill:'#9B9B9B' }} stroke="#313131" />
                      <YAxis tick={{ fontSize: 12, fill:'#9B9B9B' }} stroke="#313131" tickFormatter={v=>`¥${v}`} width={65} />
                      <RTooltip contentStyle={{ background:'#1F1F1F', border:'1px solid #313131', borderRadius:8, fontSize: 12 }} labelStyle={{ color:'#9B9B9B' }} itemStyle={{ color:'#FFFFFF' }} formatter={v=>[`¥${v}`,'Price']} />
                      <Line type="monotone" dataKey="price" stroke="#E42545" strokeWidth={2.5} dot={{ fill:'#E42545',r:4,strokeWidth:0 }} />
                      {chartPurch.map((p,i)=>(
                        <ReferenceLine key={i} y={p.price_paid_jpy} stroke="#9B9B9B" strokeDasharray="4 3"
                          label={{ value:`¥${p.price_paid_jpy}`, fill:'#9B9B9B', fontSize: 12, position:'insideTopRight' }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="jc-chart__empty">Price history builds as daily refreshes run</p>
                )}
              </div>
              {chartPurch.length > 0 && (
                <div className="jc-ledger">
                  <p className="rc-label">YOUR PURCHASES</p>
                  {chartPurch.map((p,i)=>(
                    <div key={i} className="jc-ledger__row">
                      <p className="jc-row__date">{p.order_date}</p>
                      <p>¥{p.price_paid_jpy?.toLocaleString()} × {p.qty}</p>
                      <p className="jc-ledger__total">£{(p.price_paid_jpy*p.qty*JPY_GBP).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setChartCard(null)}>Close</Button>
          <Button onClick={() => { setOrderCard(chartCard); setOrderQty(1); setChartCard(null); }} startIcon={<ShoppingCart />}>Order</Button>
        </DialogActions>
      </Dialog>

      {/* ORDER DIALOG */}
      <Dialog open={!!orderCard} onClose={() => setOrderCard(null)} fullScreen={mob}>
        <DialogTitle>
          <div className="jc-dialog-head">
            Order
            {mob && <IconButton onClick={() => setOrderCard(null)} className="jc-dialog-close"><Close /></IconButton>}
          </div>
        </DialogTitle>
        <DialogContent>
          {orderCard && (
            <div className="jc-dialog-form">
              <div className="jc-dialog-card jc-dialog-card--inset">
                <img src={orderCard.image_url} alt="" className="jc-dialog-card__img" />
                <div className="jc-dialog-card__text">
                  <p className="jc-dialog-card__name">{orderCard.pokemon_en || orderCard.name.split('【')[0]}</p>
                  <RChip rarity={orderCard.rarity} />
                  <p className="jc-row__price">¥{orderCard.price_jpy?.toLocaleString()} / £{orderCard.price_gbp}</p>
                </div>
              </div>
              <TextField label="Quantity" type="number" value={orderQty}
                onChange={e => setOrderQty(Math.max(1,parseInt(e.target.value)||1))}
                inputProps={{ min:1 }} fullWidth />
              <div className="jc-order-total">
                <p>
                  ¥{(orderCard.price_jpy*orderQty).toLocaleString()} · £{(orderCard.price_gbp*orderQty).toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOrderCard(null)} sx={{ flex:1 }}>Cancel</Button>
          <Button onClick={submitOrder} sx={{ flex:2 }}>Confirm Order</Button>
        </DialogActions>
      </Dialog>

      {/* FEATURE 6: ENHANCED RESULT DIALOG */}
      <Dialog open={!!resultCard} onClose={() => setResultCard(null)} fullScreen={mob} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div className="jc-dialog-head">
            Stream Result
            {mob && <IconButton onClick={() => setResultCard(null)} className="jc-dialog-close"><Close /></IconButton>}
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="jc-dialog-form">
            {/* Card info */}
            {resultCard && (
              <div className="jc-dialog-card jc-dialog-card--inset">
                <img src={resultCard.image_url} alt="" className="jc-dialog-card__img" />
                <div className="jc-dialog-card__text">
                  <p className="jc-dialog-card__name">{resultCard.pokemon_en || resultCard.name.split('【')[0]}</p>
                  <RChip rarity={resultCard.rarity} />
                  <p className="jc-row__price">¥{resultCard.price_jpy?.toLocaleString()} / £{resultCard.price_gbp}</p>
                </div>
              </div>
            )}

            {/* Purchase history for this card */}
            {resultPurchHistory.length > 0 && (
              <div className="jc-ledger jc-ledger--boxed">
                <p className="rc-label">PURCHASE HISTORY</p>
                {resultPurchHistory.map((p, i) => (
                  <div key={i} className="jc-ledger__row">
                    <p className="jc-row__date">{p.order_date}</p>
                    <p>¥{p.price_paid_jpy?.toLocaleString()} × {p.qty}</p>
                    <p className="jc-ledger__total">£{(p.price_paid_jpy * JPY_GBP).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Profit calculator slider */}
            <div className="jc-calculator">
              <p className="rc-label">PROFIT CALCULATOR</p>
              <Slider
                value={hammerSlider}
                onChange={(e, v) => setHammerSlider(v)}
                min={20}
                max={60}
                step={1}
                valueLabelDisplay="on"
                valueLabelFormat={v => `£${v}`}
                className="jc-slider"
              />
              <div className="jc-calculator__row">
                <p className="jc-calculator__hammer">Hammer: £{hammerSlider}</p>
                <p className="jc-profit" data-tone={resultProfit(hammerSlider, resultCard?.price_gbp) > 0 ? 'good' : 'bad'}>
                  Profit: {resultProfit(hammerSlider, resultCard?.price_gbp) > 0 ? '+' : ''}£{resultProfit(hammerSlider, resultCard?.price_gbp) || '?'}
                </p>
              </div>
            </div>

            {/* Form fields */}
            <FormControl fullWidth>
              <InputLabel>ACE Grade</InputLabel>
              <Select value={resultData.grade} onChange={e => setResultData({...resultData, grade:e.target.value})} label="ACE Grade">
                {[10,9,8,7,6,5,4,3,2,1].map(g=><MenuItem key={g} value={String(g)}>{g}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField label="Hammer Price (£)" type="number" value={resultData.hammer}
              onChange={e => setResultData({...resultData,hammer:e.target.value})}
              inputProps={{ step:'0.01' }} fullWidth />

            <TextField label="Cert Number" value={resultData.certNumber}
              onChange={e => setResultData({...resultData,certNumber:e.target.value})}
              fullWidth className="jc-cert-field" />

            <TextField label="Date" type="date" value={resultData.date}
              onChange={e => setResultData({...resultData,date:e.target.value})}
              InputLabelProps={{ shrink:true }} fullWidth />

            <TextField label="Notes" value={resultData.notes}
              onChange={e => setResultData({...resultData,notes:e.target.value})}
              multiline rows={2} fullWidth />

            <FormControlLabel
              control={<Checkbox checked={resultData.reorderFlag} onChange={e => setResultData({...resultData, reorderFlag:e.target.checked})} />}
              label={<span className="jc-check-text">Reorder this card</span>}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setResultCard(null)} sx={{ flex:1 }}>Cancel</Button>
          <Button onClick={submitResult} sx={{ flex:2 }}>Save Result</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export function PurchaseCarousel({ purchases }) {
  const [index, setIndex] = useState(0);
  const safeIndex = Math.min(index, Math.max(0, purchases.length - 1));
  const card = purchases[safeIndex];
  useEffect(() => { setIndex(0); }, [purchases]);
  if (!card) return null;
  const amount = card.price_paid_gbp == null ? null : Number(card.price_paid_gbp);
  const name = card._en || card._name || card.pid || 'Card name unavailable';
  return <div className="jc-purchase-browser" aria-label="Browse Cardrush purchase history">
    <div className="jc-purchase-browser__card">
      {card._imageUrl ? <img src={card._imageUrl} alt={name} className="jc-purchase-browser__image" /> : <div className="jc-purchase-browser__empty">Image unavailable</div>}
      <div><h3>{name}</h3><p>Order {card.order_id || 'unknown'} · {card.order_date || 'date unavailable'}</p>
        <p>Supplier price {card.price_paid_jpy == null ? 'unavailable' : `¥${Number(card.price_paid_jpy).toLocaleString('en-GB')}`} each · Quantity {card.qty ?? 'unknown'}</p>
        <p>{amount != null && Number.isFinite(amount) ? `Recorded conversion £${amount.toFixed(2)} each` : 'GBP conversion unavailable'}</p>
        <p>These are existing Cardrush records. More recent orders and Hareruya are not yet included.</p></div>
    </div>
    <div className="jc-purchase-browser__controls"><Button variant="outlined" onClick={() => setIndex(i => Math.max(0, i - 1))} disabled={safeIndex === 0}>Previous card</Button><span>Card {safeIndex + 1} of {purchases.length}</span><Button variant="outlined" onClick={() => setIndex(i => Math.min(purchases.length - 1, i + 1))} disabled={safeIndex >= purchases.length - 1}>Next card</Button></div>
  </div>;
}

// Separate component to avoid re-renders
function PurchaseList({ purchases, onChart }) {
  return purchases.map((row, i) => {
    const delta = row._currentPrice ? row._currentPrice - row.price_paid_jpy : null;
    return (
      <div key={i} className="jc-row jc-row--compact">
        {row._imageUrl
          ? <img src={row._imageUrl} alt="" loading="lazy" className="jc-thumb jc-thumb--small" />
          : <div className="jc-thumb jc-thumb--small jc-thumb--empty" />
        }
        <div className="jc-row__main">
          <p className="jc-row__title">
            {row._en || row._name?.split('【')[0] || row.pid}
          </p>
          <div className="jc-row__meta">
            {row._rarity && <RChip rarity={row._rarity} />}
            <p className="jc-row__date">{row.order_date}</p>
          </div>
        </div>
        <div className="jc-row__end">
          <p className="jc-row__paid">¥{row.price_paid_jpy?.toLocaleString()}<span className="jc-row__qty"> ×{row.qty}</span></p>
          {delta !== null && (
            <div className="jc-delta" data-tone={delta>0?'good':delta<0?'bad':'flat'}>
              {delta>0?<TrendingUp size={12}/>:delta<0?<TrendingDown size={12}/>:<TrendingFlat size={12}/>}
              <p>
                {delta>0?'+':''}{delta}¥
              </p>
            </div>
          )}
        </div>
      </div>
    );
  });
}

function getEmail() { try { return JSON.parse(localStorage.getItem('user')||'{}').email||''; } catch { return ''; } }
function today() { return new Date().toISOString().split('T')[0]; }
function fmtDate(iso) { return new Date(iso).toLocaleDateString('en-GB',{day:'2-digit',month:'short'}); }
